import React, { useState, useEffect, useMemo } from "react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SketchPicker } from "react-color";

// Mock icons since we can't import MUI icons
const VisibilityOutlinedIcon = ({ style, width }) => (
  <span style={{ ...style, fontSize: '16px' }}>👁</span>
);

const VisibilityOffOutlinedIcon = ({ style, width }) => (
  <span style={{ ...style, fontSize: '16px' }}>👁‍🗨</span>
);

// CSS for action buttons (mimicking the original styles)
const actionButtonStyles = `
.actionBtn-icon {
  cursor: pointer;
  width: 20px;
  height: 20px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.icon-plus {
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>');
}

.icon-settings {
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"></circle><path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"></path></svg>');
}

.icon-minus {
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"></line></svg>');
}

.icon-link {
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>');
}

.icon-heart {
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>');
}
`;

// Add styles to document head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = actionButtonStyles;
  document.head.appendChild(styleSheet);
}

const getOptions = (categoryTomap) => {
  return categoryTomap.map((category, index) => (
    <React.Fragment key={category.name || index}>
      <option
        value={category.name}
        id={category.name}
        data-category={JSON.stringify(category)}
      >
        {category.name}
      </option>
      {category.categories && getOTPGroup(category, category.categories)}
    </React.Fragment>
  ));
};

const getOTPGroup = (cat, list) => {
  return (
    <optgroup label={cat.name} key={cat.name}>
      {list?.map((category, index) => (
        <React.Fragment key={category.name || index}>
          <option
            value={category.name}
            id={category.name}
            data-category={JSON.stringify(category)}
          >
            {category.name}
          </option>

          {category.subCategories?.map((cat_, subIndex) => (
            <React.Fragment key={cat_.name || subIndex}>
              <option
                value={cat_.name}
                id={cat_.name}
                data-category={JSON.stringify(cat_)}
              >
                &nbsp;&nbsp;&nbsp;&nbsp; {cat_.name}
              </option>
              {cat_.children?.map((cat__, childIndex) => (
                <option
                  key={cat__.name || childIndex}
                  value={cat__.name}
                  id={cat__.name}
                  data-category={JSON.stringify(cat__)}
                >
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; {cat__.name}
                </option>
              ))}
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}
    </optgroup>
  );
};

// Utility functions for tree operations
const addNodeUnderParent = (treeData, parentId, newNode) => {
  const addToNode = (nodes) => {
    return nodes.map(node => {
      if (node.id === parentId) {
        return {
          ...node,
          haveSubMenu: true,
          children: [newNode, ...(node.children || [])]
        };
      }
      if (node.children) {
        return {
          ...node,
          children: addToNode(node.children)
        };
      }
      return node;
    });
  };
  return addToNode(treeData);
};

const removeNodeById = (treeData, nodeId) => {
  const removeFromNodes = (nodes) => {
    return nodes.filter(node => {
      if (node.id === nodeId) {
        return false;
      }
      if (node.children) {
        node.children = removeFromNodes(node.children);
      }
      return true;
    });
  };
  return removeFromNodes(treeData);
};

const updateNodeById = (treeData, nodeId, updates) => {
  const updateInNodes = (nodes) => {
    return nodes.map(node => {
      if (node.id === nodeId) {
        return { ...node, ...updates };
      }
      if (node.children) {
        return {
          ...node,
          children: updateInNodes(node.children)
        };
      }
      return node;
    });
  };
  return updateInNodes(treeData);
};

const findNodeById = (treeData, nodeId) => {
  for (const node of treeData) {
    if (node.id === nodeId) {
      return node;
    }
    if (node.children) {
      const found = findNodeById(node.children, nodeId);
      if (found) return found;
    }
  }
  return null;
};

const getDepth = (treeData, nodeId, currentDepth = 0) => {
  for (const node of treeData) {
    if (node.id === nodeId) {
      return currentDepth;
    }
    if (node.children) {
      const depth = getDepth(node.children, nodeId, currentDepth + 1);
      if (depth !== -1) return depth;
    }
  }
  return -1;
};

// Add unique IDs to tree data
const addIdsToTreeData = (treeData) => {
  let idCounter = 1;
  
  const addIds = (nodes, parentPath = []) => {
    return nodes.map((node, index) => {
      const nodeId = `node-${idCounter++}`;
      const currentPath = [...parentPath, index];
      
      return {
        ...node,
        id: nodeId,
        path: currentPath,
        children: node.children ? addIds(node.children, currentPath) : []
      };
    });
  };
  
  return addIds(treeData);
};

// Custom Popover component
const Popover = ({ children, isVisible, onClose, position = 'left' }) => {
  if (!isVisible) return null;
  
  return (
    <div 
      style={{
        position: 'fixed',
        top: '50%',
        left: position === 'left' ? '20%' : '80%',
        transform: 'translateY(-50%)',
        backgroundColor: 'white',
        border: '1px solid #ccc',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        padding: '16px',
        zIndex: 1000,
        maxWidth: '400px',
        width: '350px'
      }}
    >
      <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            color: '#666'
          }}
        >
          ×
        </button>
      </div>
      {children}
    </div>
  );
};

const TreeNode = ({ 
  node, 
  onUpdate, 
  onAdd, 
  onRemove, 
  options, 
  depth,
  isOverlay = false 
}) => {
  const [showPopover, setShowPopover] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: node.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    marginLeft: depth * 20,
  };

  const nodeStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: 'white',
    marginBottom: '4px',
    minHeight: '60px',
    ...style
  };

  if (isOverlay) {
    nodeStyle.transform = 'rotate(5deg)';
    nodeStyle.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
  }

  return (
    <>
      <div ref={setNodeRef} style={nodeStyle} {...attributes}>
        <div 
          {...listeners} 
          style={{ 
            cursor: 'grab', 
            padding: '4px', 
            marginRight: '8px',
            fontSize: '18px',
            userSelect: 'none'
          }}
        >
          ⋮⋮
        </div>
        
        <div style={{ flex: 1, marginRight: '8px' }}>
          {node.isCustomLink ? (
            <input
              type="text"
              placeholder="Enter Title"
              defaultValue={node.name ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                onUpdate(node.id, { name: value });
              }}
              style={{ 
                height: '40px', 
                width: '100%', 
                padding: '8px',
                border: '1px solid #ced4da',
                borderRadius: '4px'
              }}
            />
          ) : (
            <select
              name="category"
              value={node.name ?? ""}
              style={{ 
                height: '34px', 
                fontSize: '18px', 
                width: '100%',
                padding: '0 8px',
                border: '1px solid #ced4da',
                borderRadius: '4px'
              }}
              onChange={(event) => {
                const name = event.target.value;
                const el = document.getElementById(name);
                let category = {};
                if (el) category = JSON.parse(el.dataset.category);
                delete category.categories;
                delete category.subCategories;
                delete category.children;
                onUpdate(node.id, { ...category, name });
              }}
            >
              <option value="" disabled>
                Select Category
              </option>
              {options}
            </select>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          {/* Add default button */}
          <div
            className="icon-plus actionBtn-icon "
            title="Add default"
            style={{
              margin: "10px 4px",
              cursor: 'pointer',
              display: (!node.name || depth >= 3) && "none",
            }}
            onClick={() => onAdd(node.id, false)}
          />

          {/* Add Custom Link button */}
          <div
            className="icon-settings actionBtn-icon plus"
            title="Add Custom Link"
            style={{
              margin: "10px 4px",
              cursor: 'pointer',
              display: (!node.name || depth >= 3) && "none",
            }}
            onClick={() => onAdd(node.id, true)}
          />

          {/* Remove button */}
          <div
            className="icon-minus actionBtn-icon minus"
            style={{ margin: "10px 4px", cursor: 'pointer' }}
            title="Remove Link"
            onClick={() => onRemove(node.id)}
          />

          {/* Show in Category toggle */}
          <div
            className="actionBtn-icon"
            title="Show in Category"
            style={{ cursor: 'pointer' }}
            onClick={() => onUpdate(node.id, { showInCategory: !node.showInCategory })}
          >
            {node.showInCategory ? (
              <VisibilityOutlinedIcon
                style={{ margin: "6px 4px" }}
                width="16px"
              />
            ) : (
              <VisibilityOffOutlinedIcon
                style={{ margin: "6px 4px" }}
                width="16px"
              />
            )}
          </div>

          {/* Show in Header toggle */}
          <div
            className={!node.showInHeader ? "icon-heart" : ""}
            style={{
              margin: `${!node.showInHeader ? "10px 4px" : "5px 4px"}`,
              cursor: 'pointer',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Show in header"
            onClick={() => onUpdate(node.id, { showInHeader: !node.showInHeader })}
          >
            {node.showInHeader && (
              <span style={{ fontSize: '16px' }}>❤️</span>
            )}
          </div>

          {/* Link configuration button */}
          <div
            className="icon-link"
            style={{
              margin: "10px 4px",
              color: node.link?.link ? "#38ce3c" : undefined,
              cursor: 'pointer'
            }}
            title="Link to page"
            onClick={() => setShowPopover(true)}
          />
        </div>
      </div>

      {/* Link configuration popover */}
      <Popover 
        isVisible={showPopover} 
        onClose={() => setShowPopover(false)}
        position="left"
      >
        <div>
          <h5 style={{ marginBottom: '16px', paddingRight: '20px' }}>Add Link</h5>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
            For default keep it empty.
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
            For Sundial Links don't include Domain name in link!
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>
            For External Links add full link with "https"
          </div>
          
          <input
            type="text"
            placeholder="Enter Link"
            defaultValue={node.link?.link ?? ""}
            style={{
              width: '100%',
              padding: '8px',
              marginBottom: '12px',
              border: '1px solid #ced4da',
              borderRadius: '4px'
            }}
            onBlur={(e) => {
              const value = e.target.value;
              onUpdate(node.id, {
                link: {
                  ...node.link,
                  link: value,
                }
              });
            }}
          />
          
          <select
            name="fontWeight"
            value={node.link?.fontWeight ?? ""}
            style={{ 
              height: '34px', 
              width: '100%',
              padding: '0 8px',
              marginBottom: '12px',
              border: '1px solid #ced4da',
              borderRadius: '4px'
            }}
            onChange={(e) => {
              const value = e.target.value;
              onUpdate(node.id, {
                link: {
                  ...node.link,
                  fontWeight: value,
                }
              });
            }}
          >
            <option value="" disabled>
              Select Font Weight
            </option>
            <option value="lighter">lighter</option>
            <option value="normal">normal</option>
            <option value="bold">bold</option>
            <option value="bolder">bolder</option>
          </select>

          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <input
              type="checkbox"
              checked={node.link?.showArrow ?? false}
              onChange={(e) => {
                const value = e.target.checked;
                onUpdate(node.id, {
                  link: {
                    ...node.link,
                    showArrow: value,
                  }
                });
              }}
              style={{
                width: '14px',
                height: '14px',
                marginRight: '8px',
              }}
            />
            Show Arrow
          </label>
          
          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
            <input
              type="checkbox"
              checked={node.link?.target === "_blank"}
              onChange={(e) => {
                const value = e.target.checked;
                onUpdate(node.id, {
                  link: {
                    ...node.link,
                    target: value ? "_blank" : "_self",
                  }
                });
              }}
              style={{
                width: '14px',
                height: '14px',
                marginRight: '8px',
              }}
            />
            Open in new tab
          </label>
          
          <SketchPicker
            color={node.link?.color ?? "#221924"}
            triangle="hide"
            onChangeComplete={({ hex }) => {
              onUpdate(node.id, {
                link: {
                  ...node.link,
                  color: hex,
                }
              });
            }}
            width="280px"
          />
        </div>
      </Popover>
    </>
  );
};

const TreeView = ({ treeData, onUpdate, onAdd, onRemove, options }) => {
  const [activeId, setActiveId] = useState(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const flattenTree = (nodes, depth = 0) => {
    let flattened = [];
    
    nodes.forEach(node => {
      flattened.push({ ...node, depth });
      if (node.children && node.children.length > 0) {
        flattened = [...flattened, ...flattenTree(node.children, depth + 1)];
      }
    });
    
    return flattened;
  };

  const rebuildTree = (flatNodes) => {
    const nodeMap = new Map();
    const result = [];
    
    // Create a map of all nodes
    flatNodes.forEach(node => {
      nodeMap.set(node.id, { ...node, children: [] });
    });
    
    // Build the tree structure
    flatNodes.forEach(node => {
      const nodeData = nodeMap.get(node.id);
      
      if (node.depth === 0) {
        result.push(nodeData);
      } else {
        // Find the parent (previous node with depth - 1)
        const nodeIndex = flatNodes.findIndex(n => n.id === node.id);
        for (let i = nodeIndex - 1; i >= 0; i--) {
          if (flatNodes[i].depth === node.depth - 1) {
            const parent = nodeMap.get(flatNodes[i].id);
            parent.children.push(nodeData);
            break;
          }
        }
      }
    });
    
    return result;
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    const flatNodes = flattenTree(treeData);
    const oldIndex = flatNodes.findIndex(node => node.id === active.id);
    const newIndex = flatNodes.findIndex(node => node.id === over.id);
    
    if (oldIndex !== -1 && newIndex !== -1) {
      const newFlatNodes = arrayMove(flatNodes, oldIndex, newIndex);
      
      // Adjust depths to maintain tree structure constraints
      for (let i = 0; i < newFlatNodes.length; i++) {
        const currentNode = newFlatNodes[i];
        const prevNode = newFlatNodes[i - 1];
        
        if (prevNode) {
          // Ensure depth doesn't exceed maxDepth (3) and doesn't jump more than 1 level
          const maxAllowedDepth = Math.min(3, prevNode.depth + 1);
          currentNode.depth = Math.max(0, Math.min(currentNode.depth, maxAllowedDepth));
        } else {
          currentNode.depth = 0;
        }
      }
      
      const newTree = rebuildTree(newFlatNodes);
      onUpdate(null, newTree);
    }
    
    setActiveId(null);
  };

  const flatNodes = flattenTree(treeData);
  const activeNode = activeId ? findNodeById(treeData, activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={flatNodes.map(node => node.id)}
        strategy={verticalListSortingStrategy}
      >
        {flatNodes.map(node => (
          <TreeNode
            key={node.id}
            node={node}
            depth={node.depth}
            onUpdate={onUpdate}
            onAdd={onAdd}
            onRemove={onRemove}
            options={options}
          />
        ))}
      </SortableContext>
      
      <DragOverlay>
        {activeNode && (
          <TreeNode
            node={activeNode}
            depth={0}
            onUpdate={() => {}}
            onAdd={() => {}}
            onRemove={() => {}}
            options={options}
            isOverlay={true}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
};

function TreeHeader({
  treeData = [],
  loading = false,
  secondLevelCategories = [],
  handleSubmit = () => {},
}) {
  const [state, setState] = useState({ treeData: [] });

  useEffect(() => {
    // Add IDs to the tree data for dnd-kit
    const treeDataWithIds = addIdsToTreeData(treeData);
    setState({ treeData: treeDataWithIds });
  }, [treeData]);

  const options = useMemo(() => getOptions(secondLevelCategories), [secondLevelCategories]);

  const handleNodeUpdate = (nodeId, updates) => {
    if (nodeId === null && Array.isArray(updates)) {
      // This is a complete tree update from drag and drop
      setState({ treeData: updates });
    } else {
      // This is a single node update
      setState(prevState => ({
        treeData: updateNodeById(prevState.treeData, nodeId, updates)
      }));
    }
  };

  const handleAddNode = (parentId, isCustomLink) => {
    let idCounter = Date.now(); // Simple ID generation
    
    const newNode = {
      id: `node-${idCounter}`,
      name: "",
      slug: "",
      haveSubMenu: false,
      showInHeader: true,
      showInCategory: true,
      children: [],
      ...(isCustomLink && {
        isCustomLink: true,
        link: {
          link: "",
          color: "#221924",
          fontWeight: "normal",
          showArrow: false,
          target: "_self",
        },
      })
    };

    setState(prevState => ({
      treeData: addNodeUnderParent(prevState.treeData, parentId, newNode)
    }));
  };

  const handleRemoveNode = (nodeId) => {
    setState(prevState => ({
      treeData: removeNodeById(prevState.treeData, nodeId)
    }));
  };

  const handleAddRootNode = (isCustomLink = false) => {
    let idCounter = Date.now();
    
    const newNode = {
      id: `node-${idCounter}`,
      name: "",
      slug: "",
      haveSubMenu: false,
      showInCategory: true,
      showInHeader: true,
      children: [],
      ...(isCustomLink && {
        isCustomLink: true,
        link: {
          link: "",
          color: "#221924",
          fontWeight: "normal",
          showArrow: false,
          target: "_self",
        },
      })
    };

    setState(prevState => ({
      treeData: [...prevState.treeData, newNode]
    }));
  };

  // Convert tree data back to original format before submitting
  const convertTreeDataForSubmit = (treeData) => {
    const removeIds = (nodes) => {
      return nodes.map(node => {
        const { id, depth, path, ...nodeWithoutIds } = node;
        return {
          ...nodeWithoutIds,
          children: node.children ? removeIds(node.children) : []
        };
      });
    };
    return removeIds(treeData);
  };

  if (loading && secondLevelCategories.length === 0)
    return (
      <h2 style={{ textAlign: 'center' }}>
        <div style={{ 
          display: 'inline-block',
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          animation: 'spin 2s linear infinite'
        }}>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
        <br />
        <span>Loading...</span>
      </h2>
    );

  return (
    <div style={{ pointerEvents: loading && "none" }}>
      <div style={{ marginBottom: '16px' }}>
        <button
          style={{
            // backgroundColor: '#007bff',
            // color: 'white',
            border: 'none',
            // padding: '8px 16px',
            // margin: '8px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
          className="btn btn-primary m-2"
          onClick={() => handleSubmit(convertTreeDataForSubmit(state.treeData))}
        >
          Save Mega Menu
        </button>
        <button
          style={{
            // backgroundColor: '#007bff',
            // color: 'white',
            border: 'none',
            // padding: '8px 16px',
            // margin: '8px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
          className="btn btn-primary m-2"
          onClick={() => handleAddRootNode(false)}
        >
          Add Parent category
        </button>
        <button
          style={{
            // backgroundColor: '#007bff',
            // color: 'white',
            // border: 'none',
            // padding: '8px 16px',
            margin: '8px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
          className="btn btn-primary m-2"
          onClick={() => handleAddRootNode(true)}
        >
          Add Custom Link
        </button>
      </div>
      <div style={{ height: "90vh", overflow: "auto" }}>
        <TreeView
          treeData={state.treeData}
          onUpdate={handleNodeUpdate}
          onAdd={handleAddNode}
          onRemove={handleRemoveNode}
          options={options}
        />
      </div>
    </div>
  );
}

export default TreeHeader;